import type { ReactNode } from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "../utils/cx.js";

export interface TabItem {
  key: string;
  label: ReactNode;
  children: ReactNode;
  disabled?: boolean;
  /** Leading icon — shown in the vertical nav (and inline in horizontal labels). */
  icon?: ReactNode;
}

export interface TabsProps {
  items: TabItem[];
  activeKey?: string;
  defaultActiveKey?: string;
  onChange?: (key: string) => void;
  /** "card" draws boxed tab headers — use when the tab row must read as primary navigation. */
  variant?: "line" | "card";
  /** "vertical" renders a left-hand nav rail + content pane (e.g. a settings page). */
  orientation?: "horizontal" | "vertical";
  /** Content pinned to the end of the tab bar (e.g. a search box or actions). */
  extra?: ReactNode;
  className?: string;
}

export function Tabs({
  items,
  activeKey,
  defaultActiveKey,
  onChange,
  variant = "line",
  orientation = "horizontal",
  extra,
  className,
}: TabsProps) {
  const value = activeKey;
  const defaultValue = activeKey == null ? (defaultActiveKey ?? items[0]?.key) : undefined;

  // Only the active panel is mounted, so switching to a shorter one shrinks the
  // document and the browser clamps the scroll — throwing the user back to the
  // top of the page. Put them back where they were once the swap has laid out
  // (the rAF runs after React's commit but before the frame is painted, so the
  // clamp is never visible). Panels still need enough height for the position to
  // be reachable; see the .pd-tabs floor in the web app's styles.
  const handleChange = (key: string) => {
    const y = window.scrollY;
    onChange?.(key);
    requestAnimationFrame(() => {
      if (window.scrollY !== y) window.scrollTo(0, y);
    });
  };

  if (orientation === "vertical") {
    return (
      <TabsPrimitive.Root
        value={value}
        defaultValue={defaultValue}
        onValueChange={handleChange}
        orientation="vertical"
        className={cn("flex flex-col gap-6 sm:flex-row sm:gap-8", className)}
      >
        <TabsPrimitive.List className="flex shrink-0 gap-1 overflow-x-auto sm:w-56 sm:flex-col sm:overflow-visible">
          {items.map((it) => (
            <TabsPrimitive.Trigger
              key={it.key}
              value={it.key}
              disabled={it.disabled}
              className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium whitespace-nowrap text-muted-foreground outline-none transition-colors",
                "hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50",
                "data-[state=active]:bg-accent data-[state=active]:text-accent-foreground data-[state=active]:font-semibold",
                "sm:w-full",
              )}
            >
              {it.icon != null && <span className="shrink-0">{it.icon}</span>}
              {it.label}
            </TabsPrimitive.Trigger>
          ))}
        </TabsPrimitive.List>
        <div className="min-w-0 flex-1">
          {items.map((it) => (
            <TabsPrimitive.Content key={it.key} value={it.key} className="outline-none">
              {it.children}
            </TabsPrimitive.Content>
          ))}
        </div>
      </TabsPrimitive.Root>
    );
  }

  const card = variant === "card";
  return (
    <TabsPrimitive.Root
      value={value}
      defaultValue={defaultValue}
      onValueChange={handleChange}
      className={className}
    >
      <div className={cn("flex items-center justify-between gap-2", !card && "border-b")}>
        {/* min-w-0 lets the list shrink inside the justify-between row so a long
            tab set scrolls sideways instead of widening the whole page on mobile. */}
        <TabsPrimitive.List className={cn("inline-flex min-w-0 overflow-x-auto", card ? "gap-1" : "gap-4")}>
          {items.map((it) => (
            <TabsPrimitive.Trigger
              key={it.key}
              value={it.key}
              disabled={it.disabled}
              className={cn(
                "flex shrink-0 items-center gap-2 text-sm font-medium whitespace-nowrap text-muted-foreground transition-colors outline-none disabled:opacity-50 data-[state=active]:text-foreground",
                card
                  ? "rounded-md border border-transparent px-3 py-1.5 data-[state=active]:border-border data-[state=active]:bg-card data-[state=active]:shadow-sm"
                  : "-mb-px border-b-2 border-transparent px-1 py-2.5 data-[state=active]:border-primary",
              )}
            >
              {it.icon}
              {it.label}
            </TabsPrimitive.Trigger>
          ))}
        </TabsPrimitive.List>
        {extra != null && <div className="pb-1">{extra}</div>}
      </div>
      {items.map((it) => (
        <TabsPrimitive.Content key={it.key} value={it.key} className="pt-4 outline-none">
          {it.children}
        </TabsPrimitive.Content>
      ))}
    </TabsPrimitive.Root>
  );
}
